import React, { useMemo } from 'react'
import { Box } from '@mui/material'
import {
  ScatterChart as BaseScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ZAxis,
} from 'recharts'
import { chartWrapper, tooltipStyles, tooltipLabelStyles } from '../styles'
import Divider from '@mui/material/Divider'

export type ScatterSeriesConfig = {
  key: string
  name: string
  color: string
}

type ScatterPlotChartProps = {
  data: Record<string, any>[]
  series: ScatterSeriesConfig[]
  /** field grouping points (e.g. wheat code) */
  categoryKey: string
  /** X axis value field */
  xDataKey: string
  /** Y axis value field */
  yDataKey: string
  /** Optional axis labels */
  xLabel?: string
  yLabel?: string
  /** Show/hide legend (default false) */
  showLegend?: boolean
  /** Hide tick labels on X axis (default false) */
  hideXAxisLabels?: boolean
  /** Hide tick labels on Y axis (default false) */
  hideYAxisLabels?: boolean
  /** Control point size (default 60) */
  pointSize?: number
}

const DefaultTooltip = ({
  active,
  payload,
  labelKey,
}: {
  active?: boolean
  payload?: any[]
  labelKey: string
}) => {
  if (!active || !payload?.length) return null
  const pointData = payload[0].payload
  return (
    <div style={{ ...tooltipStyles, padding: 8 }}>
      <div style={{ ...tooltipLabelStyles, fontWeight: 'bold' }}>
        {pointData[labelKey]}
      </div>
      <Divider />
      {Object.entries(pointData).map(([key, value]) => {
        if (key === labelKey || key === 'x' || key === 'y') return null
        return (
          <div key={key} style={tooltipLabelStyles}>
            {`${key}: ${
              typeof value === 'number' ? value.toFixed(2) : String(value)
            }`}
          </div>
        )
      })}
    </div>
  )
}

export const ScatterPlotChart = ({
  data,
  series,
  categoryKey,
  xDataKey,
  yDataKey,
  xLabel,
  yLabel,
  showLegend = false,
  hideXAxisLabels = false,
  hideYAxisLabels = false,
  pointSize = 60,
}: ScatterPlotChartProps) => {
  if (!data?.length) return null

  const grouped = useMemo(() => {
    const map: Record<string, any[]> = {}
    series.forEach((s) => {
      map[s.key] = data.filter((d) => d[categoryKey] === s.key)
    })
    return map
  }, [data, series, categoryKey])

  return (
    <Box sx={chartWrapper}>
      <ResponsiveContainer width="100%" height={350}>
        <BaseScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey={xDataKey}
            tick={!hideXAxisLabels}
            label={
              xLabel
                ? { value: xLabel, position: 'insideBottom', offset: -10 }
                : undefined
            }
          />
          <YAxis
            type="number"
            dataKey={yDataKey}
            tick={!hideYAxisLabels}
            label={
              yLabel
                ? { value: yLabel, angle: -90, position: 'insideLeft' }
                : undefined
            }
          />
          <ZAxis range={[pointSize, pointSize]} />
          <Tooltip content={<DefaultTooltip labelKey={categoryKey} />} />
          {showLegend && <Legend />}
          {series.map((s) => (
            <Scatter
              key={s.key}
              name={s.name}
              data={grouped[s.key]}
              fill={s.color}
            />
          ))}
        </BaseScatterChart>
      </ResponsiveContainer>
    </Box>
  )
}
